import * as React from 'react';
import { useState } from 'react';
import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Pagination,
  Table,
  PropertyFilter,
} from '@amzn/awsui-components-react-v3';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { EmptyState } from 'src/commons/EmptyState';
import { Link } from 'src/components/common/Link';
import { createSmusSsoGroupDetailLink } from 'src/routes';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import {
  mediumPageSizePreference,
  defaultWrapLinesPreference,
  i18nStrings,
  paginationLabels,
} from 'src/commons/tables';
import { compareBy } from 'src/components/utils/sorting';
import { PageHeader } from 'src/components/common/PageHeader';
import { GroupEntry } from '../utils/smusUtil';

export interface GroupsTableProps {
  groups: GroupEntry[];
  loading: boolean;
  loadFailed: boolean;
  title: string;
  onRegister: () => void;
  onEdit: (group: GroupEntry) => void;
}

export const GroupsTable = (props: GroupsTableProps) => {
  const [selectedItems, setSelectedItems] = useState<GroupEntry[]>([]);

  const columnDefinitions: TableProps.ColumnDefinition<GroupEntry>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (e) => <Link href={createSmusSsoGroupDetailLink(e.metadataId)}>{e.name}</Link>,
      sortingField: 'name',
    },
    { id: 'groupId', header: 'Group ID', cell: (e) => e.groupId, sortingField: 'groupId' },
    { id: 'teamId', header: 'Team ID', cell: (e) => e.teamId, sortingField: 'teamId' },
    { id: 'wiki', header: 'Wiki', cell: (e) => e.wiki, sortingField: 'wiki' },
    { id: 'email', header: 'Email', cell: (e) => e.email, sortingField: 'email' },
    { id: 'cti', header: 'CTI', cell: (e) => e.ctiString, sortingField: 'cti' },
    { id: 'resolverGroup', header: 'Resolver group', cell: (e) => e.resolverGroup, sortingField: 'resolverGroup' },
    { id: 'useCase', header: 'Use case', cell: (e) => e.useCaseDescription, sortingField: 'useCase' },
    { id: 'createdBy', header: 'Created by', cell: (e) => e.createdBy, sortingField: 'createdBy' },
    { id: 'createdAt', header: 'Created at', cell: (e) => e.createdAt, sortingField: 'createdAt' },
  ];

  const contentDisplayOption: CollectionPreferencesProps.ContentDisplayOption[] = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header as string,
  }));

  const contentDisplayItem: CollectionPreferencesProps.ContentDisplayItem[] = [
    { id: 'name', visible: true },
    { id: 'teamId', visible: true },
    { id: 'createdBy', visible: true },
    { id: 'createdAt', visible: true },
  ];

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 15,
    contentDisplay: contentDisplayItem,
  });

  const buttonOptions = () => {
    return [
      {
        text: 'Actions',
        onItemClick: ({ detail }) => {
          if (detail.id === 'edit' && selectedItems.length > 0) {
            props.onEdit(selectedItems[0]);
          }
        },
        items: [
          {
            id: 'edit',
            text: 'Edit',
            disabled: props.loading || props.loadFailed || selectedItems.length === 0,
          },
        ],
      },
      {
        text: 'Register SSO group',
        variant: 'primary',
        onItemClick: () => {
          props.onRegister();
        },
        disabled: props.loading || props.loadFailed,
      },
    ];
  };

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount } = useCollection(
    props.groups,
    {
      filtering: {},
      pagination: { pageSize: preferences.pageSize },
      selection: {},
      sorting: {
        defaultState: {
          sortingColumn: {
            sortingField: 'name',
            sortingComparator: compareBy('name', 'groupId', 'teamId'),
          },
        },
      },
      propertyFiltering: {
        empty: <EmptyState title='No groups' subtitle='No groups found.' />,
        filteringProperties: [
          {
            propertyLabel: 'Group name',
            key: 'name',
            groupValuesLabel: 'Group names',
          },
          {
            propertyLabel: 'Team ID',
            key: 'teamId',
            groupValuesLabel: 'Team IDs',
          },
          {
            propertyLabel: 'Group ID',
            key: 'groupId',
            groupValuesLabel: 'Group IDs',
          },
        ],
      },
    },
  );

  return (
    <Table
      {...collectionProps}
      loading={props.loading}
      loadingText='Loading groups'
      columnDefinitions={columnDefinitions}
      items={items}
      wrapLines={preferences.wrapLines}
      selectionType='single'
      selectedItems={selectedItems}
      onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems as GroupEntry[])}
      resizableColumns={true}
      columnDisplay={preferences.contentDisplay}
      empty={<EmptyState title='No groups' subtitle='No SSO groups found.' />}
      header={<PageHeader buttons={buttonOptions()} counter={`(${props.groups.length})`} header={props.title} />}
      pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
      preferences={
        <CollectionPreferences
          title='Preferences'
          confirmLabel='Confirm'
          cancelLabel='Cancel'
          preferences={preferences}
          onConfirm={({ detail }) => setPreferences(detail)}
          pageSizePreference={mediumPageSizePreference}
          wrapLinesPreference={defaultWrapLinesPreference}
          contentDisplayPreference={{
            title: 'Group',
            options: contentDisplayOption,
          }}
        />
      }
      filter={
        <PropertyFilter
          {...propertyFilterProps}
          disabled={props.loading}
          i18nStrings={i18nStrings}
          countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
        />
      }
    />
  );
};
